"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";

import { Container } from "@/src/components/ui/Container";
import Section from "@/src/components/ui/Section";
import { Heading, Text } from "@/src/components/ui/Typography";
import { StickyColumnUntilLevel } from "@/src/components/molecules/StickyColumnUntilLevel";
import { cn } from "@/src/lib/utils";

import type { ModelDeepDiveData } from "./ModelDeepDive.types";

// A section is "active" once it has scrolled up past the line sitting 40%
// of the viewport height above the bottom edge (i.e. 60% down from the top).
const ACTIVE_ZONE_RATIO = 0.4;

interface ModelDeepDiveProps {
  model: ModelDeepDiveData;
}

export function ModelDeepDive({ model }: ModelDeepDiveProps) {
  const boundsRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string | null>(
    model.sections[0]?.id ?? null,
  );

  useEffect(() => {
    const updateActive = () => {
      const triggerLine = window.innerHeight * (1 - ACTIVE_ZONE_RATIO);
      let current = model.sections[0]?.id ?? null;
      for (const section of model.sections) {
        const el = document.getElementById(section.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= triggerLine) {
          current = section.id;
        } else {
          break;
        }
      }
      setActiveId(current);
    };

    updateActive();
    window.addEventListener("scroll", updateActive, { passive: true });
    window.addEventListener("resize", updateActive);
    return () => {
      window.removeEventListener("scroll", updateActive);
      window.removeEventListener("resize", updateActive);
    };
  }, [model.sections]);

  return (
    <Section id={model.id} className="bg-white">
      <Container>
        <div className="grid gap-12 md:grid-cols-2 md:gap-24">
          <StickyColumnUntilLevel
            boundsRef={boundsRef}
            className="flex h-fit flex-col justify-between self-start md:min-h-[calc(100vh-var(--header-height))]"
          >
            <div>
              <Text
                size="bodySMMedium"
                className="uppercase tracking-widest text-neutral-500"
              >
                Model {model.number}
              </Text>

              <Heading as="h2" size="displayLG" className="mt-3">
                {model.name}
              </Heading>

              <Heading
                size="headingMD"
                className="mt-5 max-w-125 text-neutral-700"
              >
                {model.description}
              </Heading>
            </div>

            <nav
              aria-label={`${model.name} sections`}
              className="hidden flex-col gap-3 pb-16 md:flex"
            >
              {model.sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="block w-fit"
                >
                  <Text
                    as="span"
                    size="bodySMMedium"
                    className={cn(
                      "transition",
                      activeId === section.id
                        ? "font-semibold text-brand"
                        : "text-neutral-500 hover:text-neutral-700",
                    )}
                  >
                    {section.navLabel}
                  </Text>
                </a>
              ))}
            </nav>
          </StickyColumnUntilLevel>

          <div ref={boundsRef} className="flex flex-col gap-16">
            {model.sections.map((section) => (
              <div key={section.id} id={section.id}>
                <Heading as="h3" size="headingLG">
                  {section.heading}
                </Heading>

                <Text size="bodyMD" className="mt-4 text-neutral-700">
                  {section.body}
                </Text>

                <div className="relative mt-8 aspect-4/3 w-full overflow-hidden rounded-(--radius-xl) bg-neutral-100">
                  <Image
                    src={section.image}
                    alt={section.imageAlt}
                    fill
                    className="object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
}
